import { buildWhatsAppMessage, buildWhatsAppUrl } from "../../clientViewRuntime/leadCapture/leadWhatsApp";
import { AUTOMAPA_INTAKE_WHATSAPP } from "../../clientViewRuntime/leadCapture/leadCaptureConstants";
import { buildListingDetailHref } from "../automapListing/listingViewModels";
import { buildAutomapWhatsappIntakePayload } from "./buildAutomapWhatsappIntakePayload";
import {
  buildAutomapWhatsappHandoffMessage,
  resolveAutomapWhatsappHandoffReference,
} from "./buildAutomapWhatsappHandoffMessage";
import { buildAutomapWhatsappVehicleContext } from "./buildAutomapWhatsappVehicleContext";
import { openAutomapWhatsappWidget } from "./openAutomapWhatsappWidget";
import {
  AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS,
  isAutomapaWhatsappCapabilityEnabled,
  shouldPreferAutomapaWhatsappWidget,
} from "./automapWhatsappWidgetRollout";
import { trackAutomapWhatsappWidgetEvent } from "./trackAutomapWhatsappWidgetEvent";
import { AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS } from "../../constants/automapAnalyticsEvents";

/**
 * Central AutoMapa intake WhatsApp number (REACT_APP_AUTOMAPA_INTAKE_WHATSAPP).
 * Do not hardcode this in UI components.
 */
export function getAutomapIntakeWhatsappNumber() {
  return AUTOMAPA_INTAKE_WHATSAPP || null;
}

/**
 * Build a wa.me URL for a listing without opening it (vehicle CTA fallback).
 */
export function buildAutomapWhatsappUrlForListing({
  listing,
  slug = "automap",
  locale = null,
  leadType = "listing_inquiry",
  sourceAction = "contactar_whatsapp",
  phoneNumber = null,
} = {}) {
  const detailPath = listing ? buildListingDetailHref(slug, listing, locale) : null;
  const detailUrl =
    typeof window !== "undefined" && detailPath
      ? `${window.location.origin}${detailPath}`
      : detailPath || (typeof window !== "undefined" ? window.location.href : null);

  const message = buildWhatsAppMessage({
    leadType,
    sourceAction,
    listing,
    detailUrl,
  });

  return buildWhatsAppUrl({
    phoneNumber: phoneNumber || getAutomapIntakeWhatsappNumber(),
    message,
  });
}

/**
 * Build a contextual wa.me URL from widget intake state (M5 handoff).
 */
export function buildAutomapWhatsappHandoffUrl({
  form = null,
  vehicleContext = null,
  widgetContext = null,
  intakePayload = null,
  phase = "draft",
  submissionResult = null,
  referenceId = null,
  phoneNumber = null,
  formatMoney = null,
  displayCurrency = null,
  locale = null,
  copy = null,
} = {}) {
  const payload =
    intakePayload ||
    buildAutomapWhatsappIntakePayload({
      form: form || {},
      vehicleContext,
      widgetContext,
    });

  const resolvedReference =
    referenceId || resolveAutomapWhatsappHandoffReference(submissionResult);

  const message = buildAutomapWhatsappHandoffMessage({
    payload,
    phase,
    referenceId: resolvedReference,
    formatMoney,
    displayCurrency,
    locale,
    copy,
  });

  return buildWhatsAppUrl({
    phoneNumber: phoneNumber || getAutomapIntakeWhatsappNumber(),
    message,
  });
}

/**
 * Open the contextual WhatsApp handoff in a new tab / native app.
 * @returns {{ url: string|null, message: string|null, opened: boolean }}
 */
export function openAutomapWhatsappHandoff(options = {}) {
  if (typeof window === "undefined") {
    return { url: null, message: null, opened: false };
  }

  const payload =
    options.intakePayload ||
    buildAutomapWhatsappIntakePayload({
      form: options.form || {},
      vehicleContext: options.vehicleContext,
      widgetContext: options.widgetContext,
    });

  const phase = options.phase || "draft";
  const referenceId =
    options.referenceId || resolveAutomapWhatsappHandoffReference(options.submissionResult);
  const message = buildAutomapWhatsappHandoffMessage({
    payload,
    phase,
    referenceId,
    formatMoney: options.formatMoney,
    displayCurrency: options.displayCurrency,
    locale: options.locale,
    copy: options.copy,
  });
  const url = buildWhatsAppUrl({
    phoneNumber: options.phoneNumber || getAutomapIntakeWhatsappNumber(),
    message,
  });

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.info("[AutoMapa WhatsApp handoff]", {
      phase,
      entryPoint: payload?.entryPoint || null,
      hasVehicle: Boolean(payload?.vehicle?.id),
      hasReference: Boolean(referenceId),
    });
  }

  if (!url) {
    return { url: null, message, opened: false };
  }

  window.open(url, "_blank", "noopener,noreferrer");
  return { url, message, opened: true };
}

/**
 * Open the AutoMapa WhatsApp intake path for a listing.
 * Prefers the on-site widget (M2). Falls back to wa.me when the widget is unavailable.
 */
export function openAutomapWhatsappBotForListing({
  listing,
  slug = "automap",
  locale = null,
  currency = "COP",
  formatMoney = null,
  dealer = null,
  leadType = "listing_inquiry",
  sourceAction = "contactar_whatsapp",
  source = null,
  preferWidget = true,
} = {}) {
  if (typeof window === "undefined") {
    return null;
  }

  const vehicleContext = buildAutomapWhatsappVehicleContext({
    listing,
    slug,
    locale,
    currency,
    formatMoney,
    dealer,
  });

  const pagePath = typeof window !== "undefined" ? window.location.pathname : null;
  const prefer =
    preferWidget &&
    shouldPreferAutomapaWhatsappWidget({ pagePath }) &&
    isAutomapaWhatsappCapabilityEnabled(AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.VEHICLE_CTA);

  if (prefer) {
    const opened = openAutomapWhatsappWidget({
      listing: listing || null,
      vehicleContext,
      leadType,
      sourceAction,
      source: source || sourceAction || "vehicle_whatsapp_cta",
      slug,
      locale,
      currency,
    });
    if (opened) {
      return { mode: "widget", vehicleContext, url: null };
    }
  } else if (preferWidget) {
    trackAutomapWhatsappWidgetEvent(AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS.WIDGET_FALLBACK_WA_ME, {
      vehicleContext,
      status: "flag_disabled",
      errorCode: "widget_flag_disabled",
      listingId: vehicleContext?.listingId || listing?.id || null,
      dealerId: vehicleContext?.dealerId || dealer?.id || null,
    });
  }

  const url = buildAutomapWhatsappUrlForListing({
    listing,
    slug,
    locale,
    leadType,
    sourceAction,
  });
  if (!url) {
    trackAutomapWhatsappWidgetEvent(AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS.ERROR, {
      vehicleContext,
      status: "wa_me_url_failed",
      errorCode: "whatsapp_link_failed",
      safeMessage: "No se pudo generar el enlace de WhatsApp.",
    });
    return null;
  }

  window.open(url, "_blank", "noopener,noreferrer");
  return { mode: "wa_me", vehicleContext, url };
}
